import { FUSO_ESCRITORIO } from 'dashboard/helper/fusoEscritorio';
/**
 * A GUARDA DO MODAL — o que impede um modal de levar embora o que a pessoa
 * escreveu (05/10/2026).
 *
 * O caso que trouxe isto: o Eduardo escrevia uma movimentação, puxou a alça
 * do campo para aumentar o texto, soltou o mouse fora do modal — e o modal
 * fechou com tudo dentro. Reproduzido no localhost no mesmo dia.
 *
 * A causa é do navegador, não do Eduardo: quando o botão é APERTADO dentro do
 * modal e SOLTO fora, o "clique" vai para o ancestral comum dos dois pontos —
 * o fundo escuro. E o fundo escuro, em 45 modais do produto, fecha no clique
 * (`@click.self`). O modal antigo do Chatwoot já se protegia disso; os
 * nossos não.
 *
 * Mora aqui, chamada pela diretiva `v-modal-acessivel`, porque TODOS os 45
 * modais que fecham no fundo já usam a diretiva: uma correção vale para todos.
 */

// O fundo é um ancestral da caixa (`.advup-modal-fundo > .advup-modal`).
// Clique cujo alvo é um ancestral da caixa, e não a caixa nem nada dentro
// dela, é um clique "no fundo".
const ehNoFundo = (caixa, alvo) =>
  alvo instanceof Node &&
  alvo !== caixa &&
  !caixa.contains(alvo) &&
  alvo.contains(caixa);

// 2. O QUE CONTA COMO "ALGO ESCRITO". Só campo de texto em que a pessoa
//    entrou e cujo valor mudou desde então — preenchimento automático que
//    chega depois não conta, e busca (filtrar uma lista) não é obra de
//    ninguém. `data-sem-guarda` tira um campo (ou um bloco) da conta.
const CAMPO_DE_TEXTO = [
  'textarea',
  'input:not([type])',
  'input[type="text"]',
  'input[type="email"]',
  'input[type="tel"]',
  'input[type="url"]',
  'input[type="number"]',
  '[contenteditable=""]',
  '[contenteditable="true"]',
].join(',');

const contaComoEscrita = el =>
  el instanceof Element &&
  el.matches(CAMPO_DE_TEXTO) &&
  !el.matches('.advup-busca__campo') &&
  !el.closest('[data-sem-guarda]');

const valorDe = el => (el.isContentEditable ? el.innerText : el.value) || '';

// Desistir = o X do topo, o X da gaveta, ou o botão que diz "Cancelar" ou
// "Fechar" — a forma de desistir em todos os modais do produto. `data-desistir`
// marca outro botão; `data-sem-guarda` libera um botão da pergunta.
const ehDesistir = botao =>
  botao.matches(
    '.advup-modal__fechar, .advup-gaveta__fechar, [data-desistir]'
  ) || /^(cancelar|fechar)$/i.test(botao.textContent.trim());

const TEXTO_PADRAO = {
  rascunho: 'Rascunho recuperado de {quando}.',
  descartar_rascunho: 'Descartar rascunho',
  titulo: 'Descartar o que você escreveu?',
  sub: 'O que está neste formulário ainda não foi salvo.',
  continuar: 'Continuar escrevendo',
  descartar: 'Descartar',
};

export const guardarModal = (caixa, { t } = {}) => {
  const texto = (chave, valores = {}) => {
    const traduzido = t?.(`MODAL_GUARDA.${chave.toUpperCase()}`, valores);
    const base =
      traduzido && !traduzido.startsWith('MODAL_GUARDA')
        ? traduzido
        : TEXTO_PADRAO[chave];
    return base.replace(/\{(\w+)\}/g, (_, k) => valores[k] ?? '');
  };

  // ── 2. o que a pessoa escreveu ──────────────────────────────────────────
  const iniciais = new Map();
  const aoEntrar = evento => {
    const el = evento.target;
    if (contaComoEscrita(el) && !iniciais.has(el)) {
      iniciais.set(el, valorDe(el));
    }
  };
  caixa.addEventListener('focusin', aoEntrar);

  const sujo = () =>
    [...iniciais].some(
      ([el, antes]) =>
        el.isConnected &&
        caixa.contains(el) &&
        valorDe(el).trim() !== antes.trim()
    );

  // A pergunta "Descartar?", quando aberta (item 2, mais abaixo).
  let pergunta = null;

  // ── 3. o rascunho ───────────────────────────────────────────────────────
  //
  // Liga com `data-rascunho="chave"` na caixa, nos modais de texto longo. O
  // que a pessoa escreve fica guardado neste navegador; se o modal fechar por
  // qualquer motivo (página recarregada, aba fechada, travou), ao reabrir o
  // texto volta, com a faixa "Rascunho recuperado de 14:32 · Descartar".
  //
  // Só para CRIAR: se algum campo de texto longo já abre preenchido, é uma
  // edição — e um rascunho da edição do registro A não pode aparecer no B.
  // Edição continua protegida pela pergunta do item 2.
  const chave = caixa.dataset.rascunho
    ? `advup-rascunho:${caixa.dataset.rascunho}`
    : null;
  const VALIDADE = 7 * 24 * 3600 * 1000;
  let rascunhoLigado = !!chave;
  let salvarTimer = 0;
  let faixa = null;

  // Só o texto longo (`textarea`) — é o que dói perder. Campo curto pode ser
  // busca com escolha (o cliente, a pasta): devolver o texto digitado sem a
  // escolha deixaria o formulário incoerente.
  const camposDeTexto = () =>
    [...caixa.querySelectorAll('textarea')].filter(
      el => contaComoEscrita(el) && !pergunta?.contains(el)
    );

  const lerRascunho = () => {
    try {
      return JSON.parse(localStorage.getItem(chave) || 'null');
    } catch {
      return null;
    }
  };

  const apagarRascunho = () => {
    if (!chave) return;
    clearTimeout(salvarTimer);
    try {
      localStorage.removeItem(chave);
    } catch {
      // sem armazenamento: não havia rascunho
    }
  };

  // O tremor: o modal avisa que não vai fechar sem a pessoa decidir.
  const tremer = () => {
    caixa.classList.remove('advup-modal--tremor');
    // força o recomeço da animação se ela já estava rodando
    // eslint-disable-next-line no-unused-expressions
    caixa.offsetWidth;
    caixa.classList.add('advup-modal--tremor');
    setTimeout(() => caixa.classList.remove('advup-modal--tremor'), 450);
  };

  // A pergunta, DENTRO do modal (o diálogo nativo do navegador tira a
  // pessoa da tela e não segue o visual). O resto do modal fica inerte
  // enquanto ela decide.
  const fecharPergunta = () => {
    if (!pergunta) return;
    pergunta.remove();
    pergunta = null;
    [...caixa.children].forEach(filho => filho.removeAttribute('inert'));
  };

  const perguntar = aoDescartar => {
    if (pergunta) return;
    [...caixa.children].forEach(filho => filho.setAttribute('inert', ''));
    // A pergunta cobre a caixa: precisa de uma caixa posicionada.
    if (getComputedStyle(caixa).position === 'static') {
      caixa.style.position = 'relative';
    }
    pergunta = document.createElement('div');
    pergunta.className = 'advup-guarda';
    pergunta.setAttribute('role', 'alertdialog');
    pergunta.setAttribute('aria-modal', 'true');
    const tituloId = `guarda-${Math.random().toString(36).slice(2, 9)}`;
    pergunta.setAttribute('aria-labelledby', tituloId);
    pergunta.innerHTML = `
      <div class="advup-guarda__cartao">
        <p class="advup-guarda__titulo" id="${tituloId}"></p>
        <p class="advup-guarda__sub"></p>
        <div class="advup-guarda__acoes">
          <button type="button" class="advup-btn advup-btn--vazado" data-guarda="continuar"></button>
          <button type="button" class="advup-btn advup-btn--perigo" data-guarda="descartar"></button>
        </div>
      </div>`;
    pergunta.querySelector('.advup-guarda__titulo').textContent =
      texto('titulo');
    pergunta.querySelector('.advup-guarda__sub').textContent = texto('sub');
    const continuar = pergunta.querySelector('[data-guarda="continuar"]');
    const descartar = pergunta.querySelector('[data-guarda="descartar"]');
    continuar.textContent = texto('continuar');
    descartar.textContent = texto('descartar');
    continuar.addEventListener('click', () => {
      fecharPergunta();
    });
    descartar.addEventListener('click', () => {
      fecharPergunta();
      iniciais.clear();
      apagarRascunho();
      aoDescartar();
    });
    caixa.appendChild(pergunta);
    continuar.focus();
  };

  // X e Cancelar passam pela pergunta quando há algo escrito.
  let liberado = false;
  let ultimoCliqueDeAcao = 0;
  const aoClicarDentro = evento => {
    if (liberado || pergunta?.contains(evento.target)) return;
    const botao = evento.target.closest?.('button');
    if (
      !botao ||
      !caixa.contains(botao) ||
      botao.closest('[data-sem-guarda]')
    ) {
      return;
    }
    if (!ehDesistir(botao)) {
      ultimoCliqueDeAcao = Date.now();
      return;
    }
    if (!sujo()) return;
    evento.preventDefault();
    evento.stopImmediatePropagation();
    perguntar(() => {
      liberado = true;
      botao.click();
      liberado = false;
    });
  };
  caixa.addEventListener('click', aoClicarDentro, true);

  // 1. SÓ FECHA SE O CLIQUE COMEÇOU E TERMINOU FORA.
  //    Arrastar de dentro para fora (alça do campo, seleção de texto que
  //    passa da borda) nunca fecha — o mesmo das bibliotecas de interface
  //    sérias. O clique só segue para o fundo se o botão foi apertado nele.
  let apertouNoFundo = false;
  const aoApertar = evento => {
    apertouNoFundo = ehNoFundo(caixa, evento.target);
  };

  const aoClicar = evento => {
    if (!ehNoFundo(caixa, evento.target)) return;
    const comecouFora = apertouNoFundo;
    apertouNoFundo = false;
    if (comecouFora && !sujo()) return;
    evento.stopPropagation();
    evento.preventDefault();
    // Clique de verdade no fundo, com algo escrito: não fecha, treme.
    if (comecouFora) tremer();
  };

  document.addEventListener('pointerdown', aoApertar, true);
  document.addEventListener('mousedown', aoApertar, true);
  document.addEventListener('click', aoClicar, true);

  const gravarRascunho = () => {
    if (!rascunhoLigado) return;
    if (!sujo()) {
      apagarRascunho();
      return;
    }
    try {
      localStorage.setItem(
        chave,
        JSON.stringify({
          em: Date.now(),
          valores: camposDeTexto().map(el => el.value),
        })
      );
    } catch {
      // armazenamento cheio ou bloqueado: a pergunta do item 2 ainda protege
    }
  };

  const aoDigitar = evento => {
    if (!rascunhoLigado || !contaComoEscrita(evento.target)) return;
    clearTimeout(salvarTimer);
    salvarTimer = setTimeout(gravarRascunho, 400);
  };
  caixa.addEventListener('input', aoDigitar);

  // Valor posto como se a pessoa tivesse digitado: o `v-model` recebe.
  const definirValor = (el, valor) => {
    const descritor = Object.getOwnPropertyDescriptor(
      Object.getPrototypeOf(el),
      'value'
    );
    descritor.set.call(el, valor);
    el.dispatchEvent(new Event('input', { bubbles: true }));
  };

  const quandoFoi = em => {
    const data = new Date(em);
    const hora = data.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: FUSO_ESCRITORIO,
    });
    const hoje =
      new Date().toLocaleDateString('pt-BR', {
        timeZone: FUSO_ESCRITORIO,
      }) === data.toLocaleDateString('pt-BR', { timeZone: FUSO_ESCRITORIO });
    if (hoje) return hora;
    const dia = data.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      timeZone: FUSO_ESCRITORIO,
    });
    return `${dia}, ${hora}`;
  };

  const mostrarFaixa = (em, aoDescartarRascunho) => {
    faixa = document.createElement('div');
    faixa.className = 'advup-rascunho';
    faixa.setAttribute('role', 'status');
    faixa.innerHTML =
      '<i class="i-lucide-history" aria-hidden="true"></i><span></span><button type="button" class="advup-rascunho__descartar" data-sem-guarda></button>';
    faixa.querySelector('span').textContent = texto('rascunho', {
      quando: quandoFoi(em),
    });
    const botao = faixa.querySelector('button');
    botao.textContent = texto('descartar_rascunho');
    botao.addEventListener('click', () => {
      aoDescartarRascunho();
      faixa?.remove();
      faixa = null;
    });
    const corpo = caixa.querySelector('.advup-modal__corpo');
    if (corpo) corpo.prepend(faixa);
    else caixa.insertBefore(faixa, caixa.children[1] || null);
  };

  // Devolve `true` quando já decidiu (restaurou, não havia, ou é edição);
  // `false` quando os campos ainda não chegaram.
  const restaurar = () => {
    if (!chave) return true;
    const campos = camposDeTexto();
    if (!campos.length) return false;
    if (campos.some(el => el.tagName === 'TEXTAREA' && el.value.trim())) {
      rascunhoLigado = false;
      return true;
    }
    const salvo = lerRascunho();
    if (!salvo) return true;
    const valido =
      Date.now() - salvo.em < VALIDADE &&
      Array.isArray(salvo.valores) &&
      salvo.valores.length === campos.length;
    if (!valido) {
      apagarRascunho();
      return true;
    }
    const antes = campos.map(valorDe);
    let restaurou = false;
    campos.forEach((el, i) => {
      const valor = salvo.valores[i];
      if (typeof valor !== 'string' || !valor.trim() || valor === el.value) {
        return;
      }
      if (!iniciais.has(el)) iniciais.set(el, antes[i]);
      definirValor(el, valor);
      restaurou = true;
    });
    if (restaurou) {
      mostrarFaixa(salvo.em, () => {
        campos.forEach((el, i) => {
          if (el.isConnected) definirValor(el, antes[i]);
        });
        apagarRascunho();
      });
    }
    return true;
  };

  // ── 5. o campo que cresce sozinho ───────────────────────────────────────
  //
  // O gesto que fechou o modal do Eduardo começou na alça de esticar o campo.
  // Campo que cresce enquanto a pessoa escreve dispensa a alça: ela some, e
  // o campo vai da altura de sempre até 45% da tela — dali para a frente,
  // rola por dentro. `data-sem-crescer` deixa um campo como está.
  const crescer = campo => {
    if (campo.tagName !== 'TEXTAREA' || campo.closest('[data-sem-crescer]')) {
      return;
    }
    if (!campo.dataset.alturaBase) {
      const altura = campo.offsetHeight;
      if (!altura) return; // ainda sem desenho (escondido): tenta depois
      campo.dataset.alturaBase = String(altura);
      campo.style.resize = 'none';
    }
    const base = Number(campo.dataset.alturaBase);
    const bordas = campo.offsetHeight - campo.clientHeight;
    const maximo = Math.round(window.innerHeight * 0.45);
    // Medir com `auto` faz o corpo do modal pular de rolagem: guarda e volta.
    const rolagem = campo.closest('.advup-modal__corpo') || caixa;
    const topo = rolagem.scrollTop;
    campo.style.height = 'auto';
    const precisa = campo.scrollHeight + bordas;
    const alvo = Math.min(Math.max(precisa, base), maximo);
    campo.style.height = `${alvo}px`;
    campo.style.overflowY = precisa > maximo ? 'auto' : 'hidden';
    rolagem.scrollTop = topo;
  };

  const aoMexerNoCampo = evento => crescer(evento.target);
  caixa.addEventListener('input', aoMexerNoCampo);
  caixa.addEventListener('focusin', aoMexerNoCampo);

  const crescerTodos = () =>
    caixa.querySelectorAll('textarea').forEach(campo => crescer(campo));

  // Os campos podem chegar depois de o modal montar (como o foco, na
  // diretiva): tenta agora e mais duas vezes. Os campos que abrem com texto
  // (edição, rascunho) já crescem para mostrar tudo.
  let decidiuRascunho = false;
  const tentativas = [0, 300, 900].map(ms =>
    setTimeout(() => {
      if (!decidiuRascunho) decidiuRascunho = restaurar();
      crescerTodos();
    }, ms)
  );

  return {
    sujo,
    perguntar,
    // O Escape: com a pergunta aberta, volta a escrever; com algo escrito,
    // pergunta; sem nada, deixa fechar. Devolve se tratou.
    aoEscape(fechar) {
      if (pergunta) {
        fecharPergunta();
        return true;
      }
      if (!sujo()) return false;
      perguntar(fechar);
      return true;
    },
    soltar() {
      tentativas.forEach(clearTimeout);
      // Fechou depois de clicar numa ação (salvar, criar): o rascunho cumpriu
      // o papel. Fechou com algo escrito sem ação nenhuma (a página trocou,
      // o modal sumiu): o rascunho fica para quando abrir de novo.
      if (rascunhoLigado) {
        clearTimeout(salvarTimer);
        const salvou = Date.now() - ultimoCliqueDeAcao < 15000;
        if (salvou || !sujo()) apagarRascunho();
        else gravarRascunho();
      }
      faixa?.remove();
      caixa.removeEventListener('input', aoDigitar);
      caixa.removeEventListener('input', aoMexerNoCampo);
      caixa.removeEventListener('focusin', aoMexerNoCampo);
      fecharPergunta();
      caixa.removeEventListener('focusin', aoEntrar);
      caixa.removeEventListener('click', aoClicarDentro, true);
      document.removeEventListener('pointerdown', aoApertar, true);
      document.removeEventListener('mousedown', aoApertar, true);
      document.removeEventListener('click', aoClicar, true);
    },
  };
};

export default guardarModal;
